import { motion, AnimatePresence } from "framer-motion";
import { X, AlertTriangle } from "lucide-react";
import { overlayCloseHandlers } from "../../lib/overlayClose";

export function Modal({ open, onClose, title, children, testid, size = "md" }) {
  const sizeCls = size === "2xl" ? "max-w-6xl w-full" : size === "xl" ? "max-w-4xl" : size === "lg" ? "max-w-2xl" : "max-w-lg";
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/40 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          {...overlayCloseHandlers(onClose)}
          data-testid={testid ? `${testid}-overlay` : undefined}
        >
          <motion.div
            className={`bg-white rounded-xl shadow-xl w-full ${sizeCls} max-h-[90vh] overflow-y-auto`}
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            data-testid={testid}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-tint sticky top-0 bg-white">
              <h3 className="text-navy font-bold text-lg">{title}</h3>
              <button onClick={onClose} className="text-muted2 hover:text-navy" data-testid={testid ? `${testid}-close` : undefined}>
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">{children}</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Mascaras de input reutilizaveis
export const masks = {
  // Codigo postal PT: NNNN-NNN (max 8 chars com hifen)
  cp_pt: (raw) => {
    const digits = String(raw || "").replace(/\D/g, "").slice(0, 7);
    if (digits.length <= 4) return digits;
    return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  },
};

export function Field({ label, value, onChange, placeholder, type = "text", testid, mask, maxLength, pattern, title }) {
  const handle = (v) => {
    const next = mask ? mask(v) : v;
    onChange(next);
  };
  return (
    <div>
      <label className="block text-sm font-semibold text-navy mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => handle(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        pattern={pattern}
        title={title}
        data-testid={testid}
        className="w-full px-3 py-2 border border-tint rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue"
      />
    </div>
  );
}

export function TextArea({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-navy mb-1">{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={3}
        data-testid={testid}
        className="w-full px-3 py-2 border border-tint rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue"
      />
    </div>
  );
}

// Devolve data/hora em formato pt-PT, SEMPRE no fuso de Lisboa (independente do
// fuso do dispositivo que esta a ver o Hub) - usado em todos os logs/timestamps.
export function fmtDT(iso, withSeconds = false) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "-";
  const opts = { timeZone: "Europe/Lisbon", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" };
  if (withSeconds) opts.second = "2-digit";
  return d.toLocaleString("pt-PT", opts);
}

export function fmtD(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("pt-PT", { timeZone: "Europe/Lisbon" });
}

export function Selecter({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-navy mb-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full px-3 py-2 border border-tint rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue"
      >
        {options.map((o) => (
          <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
        ))}
      </select>
    </div>
  );
}

// Deteta chavetas por preencher em textos com variaveis de merge ({var} ou {{var}}):
// desequilibradas, vazias, formato errado (simples vs dupla) ou variavel desconhecida.
export function braceIssues(text, knownKeys = [], style = "double") {
  if (!text) return null;
  const openCount = (text.match(/\{/g) || []).length;
  const closeCount = (text.match(/\}/g) || []).length;
  if (openCount !== closeCount) return "Chavetas desequilibradas - verifique se cada { tem um } a fechar.";
  if (openCount === 0) return null;

  if (style === "double") {
    const stripped = text.replace(/\{\{\s*[a-zA-Z0-9_.-]*\s*\}\}/g, "");
    if (/\{[^{}]*\}/.test(stripped)) {
      const m = stripped.match(/\{([a-zA-Z0-9_.-]*)\}/);
      const key = (m && m[1].trim()) || "variavel";
      return `Use chaveta dupla {{${key}}} em vez de chaveta simples.`;
    }
    for (const m of text.matchAll(/\{\{\s*([a-zA-Z0-9_.-]*)\s*\}\}/g)) {
      const key = m[1].trim();
      if (!key) return "Chaveta dupla vazia {{}} - indique o nome da variavel.";
      if (knownKeys.length && !knownKeys.includes(key)) return `Variavel "{{${key}}}" desconhecida - verifique a lista de variaveis.`;
    }
    return null;
  }

  const doubleMatches = text.match(/\{\{\s*[a-zA-Z0-9_.-]*\s*\}\}/g);
  if (doubleMatches && doubleMatches.length) {
    const key = doubleMatches[0].replace(/[{}]/g, "").trim() || "variavel";
    return `Use chaveta simples {${key}} em vez de chaveta dupla.`;
  }
  for (const m of text.matchAll(/\{([a-zA-Z0-9_.-]*)\}/g)) {
    const key = m[1].trim();
    if (!key) return "Chaveta vazia {} - indique o nome da variavel.";
    if (knownKeys.length && !knownKeys.includes(key)) return `Variavel "{${key}}" desconhecida - use a lista "Variaveis do contacto".`;
  }
  return null;
}

export function BracesWarning({ issue, testid }) {
  if (!issue) return null;
  return (
    <p className="text-[10px] text-amber-600 mt-0.5 flex items-start gap-1" data-testid={testid}>
      <AlertTriangle size={10} className="shrink-0 mt-0.5" /> {issue}
    </p>
  );
}
